package com.siriusxm.pia.views.news

import androidx.compose.runtime.*
import com.siriusxm.cmc.smithy.MarkdownText
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.UserGroup
import com.siriusxm.pia.components.*
import com.siriusxm.unifiedcontent.cmc.NewsItem
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.rows
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Styles for the news page components.
 */
object NewsPageStyles : StyleSheet() {
    val pageContainer by style {
        maxWidth(900.px)
        marginLeft(CSSKeywordValue("auto").unsafeCast<CSSNumeric>())
        marginRight(CSSKeywordValue("auto").unsafeCast<CSSNumeric>())
    }

    val newsItem by style {
        marginBottom(1.5.cssRem)
    }

    val newsItemHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.FlexStart)
        marginBottom((0.75).cssRem)
    }

    val newsItemActions by style {
        display(DisplayStyle.Flex)
        gap(8.px)
    }

    val actionButton by style {
        cursor("pointer")
        padding(4.px)
        borderRadius(4.px)
        color(SXMUI.subtleTextColor.value())

        self + hover style {
            backgroundColor(SXMUI.borderDividerDefault.value())
            color(SXMUI.textColor.value())
        }
    }

    val deleteButton by style {
        self + hover style {
            color(Color.red)
        }
    }

    val newsItemContent by style {
        // Style markdown content
        type("h1") style {
            fontSize((1.2).cssRem)
            marginTop(1.cssRem)
            marginBottom((0.5).cssRem)
        }
        type("h2") style {
            fontSize((1.1).cssRem)
            marginTop((0.75).cssRem)
            marginBottom((0.4).cssRem)
        }
        type("h3") style {
            fontSize(1.cssRem)
            marginTop((0.6).cssRem)
            marginBottom((0.3).cssRem)
        }
        type("ul") style {
            marginTop((0.5).cssRem)
            marginBottom((0.5).cssRem)
            paddingLeft((1.5).cssRem)
        }
        type("li") style {
            marginBottom((0.3).cssRem)
        }
        type("a") style {
            color(SXMUI.brandColor.value())
        }
        type("p") style {
            marginTop(0.px)
            marginBottom((0.75).cssRem)
        }
        type("code") style {
            backgroundColor(SXMUI.borderDividerDefault.value())
            padding(2.px, 6.px)
            borderRadius(4.px)
            fontSize((0.9).em)
        }
        type("pre") style {
            backgroundColor(SXMUI.borderDividerDefault.value())
            padding((0.75).cssRem)
            borderRadius(6.px)
            overflow("auto")
        }
    }

    val newsItemMetadata by style {
        fontSize((0.85).cssRem)
        color(SXMUI.subtleTextColor.value())
        marginTop(1.cssRem)
        paddingTop((0.75).cssRem)
        borderTop(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
    }

    val emptyState by style {
        padding(3.cssRem)
        textAlign("center")
        color(SXMUI.subtleTextColor.value())
    }

    val loadMoreButton by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        marginTop(1.cssRem)
    }

    val editorTextarea by style {
        width(100.percent)
        minHeight(200.px)
        fontFamily("monospace")
        fontSize((0.9).cssRem)
        padding((0.5).cssRem)
        border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
        borderRadius(4.px)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        color(SXMUI.textColor.value())
        property("resize", "vertical")
    }

}

/**
 * The full news page displaying all news items.
 *
 * This page shows the complete news feed with all items, newest first.
 * Administrators can create new items, edit existing items, or delete items.
 *
 * Features:
 * - Full list of news items with pagination
 * - Create button for admins
 * - Edit and delete buttons on each item for admins
 * - Markdown rendering for content
 * - Load more button for pagination
 */
@Composable
fun newsPage() {
    Style(NewsPageStyles)

    val news = rememberNews()
    val scope = rememberCoroutineScope()
    val isAdmin = Application.viewer.isInGroup(UserGroup.AggregatorAdmin)

    var showCreateDialog by remember { mutableStateOf(false) }
    var editingItem by remember { mutableStateOf<NewsItem?>(null) }
    var deletingItem by remember { mutableStateOf<NewsItem?>(null) }

    // Load all items on mount
    LaunchedEffect(Unit) {
        val cmcApiUrl = Application.configuration.cmcApiUrl
        if (cmcApiUrl != null) {
            news.initialize(cmcApiUrl) { config ->
                Application.context.installAuth(config)
            }
            news.loadAllItems(refresh = true)
        }
    }

    serviceView {
        serviceContentView {
            Div({ classes(NewsPageStyles.pageContainer) }) {
                box({
                    title = "News"
                    if (isAdmin) {
                        action {
                            this.title = "New"
                            this.primary = true
                            this.action {
                                showCreateDialog = true
                            }
                        }
                    }
                }) {
                    when {
                        news.isLoading.value && news.items.value.isEmpty() -> {
                            Div({
                                style {
                                    padding(3.cssRem)
                                    textAlign("center")
                                }
                            }) {
                                spinner(size = Size.LARGE)
                            }
                        }
                        news.items.value.isEmpty() -> {
                            Div({ classes(NewsPageStyles.emptyState) }) {
                                if (isAdmin) {
                                    Text("No news items yet. Click \"New\" to create one.")
                                } else {
                                    Text("No news at this time.")
                                }
                            }
                        }
                        else -> {
                            // News items list
                            news.items.value.forEach { item ->
                                newsItemCard(
                                    item = item,
                                    isAdmin = isAdmin,
                                    onEdit = { editingItem = item },
                                    onDelete = { deletingItem = item }
                                )
                            }

                            // Load more button
                            if (news.nextToken.value != null) {
                                Div({ classes(NewsPageStyles.loadMoreButton) }) {
                                    button {
                                        title = if (news.isLoading.value) "Loading..." else "Load More"
                                        action {
                                            news.loadAllItems(refresh = false)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Create dialog
    if (showCreateDialog) {
        newsEditDialog(
            item = null,
            onSave = { markdown ->
                scope.launch {
                    news.createItem(markdown)
                    showCreateDialog = false
                }
            },
            onClose = { showCreateDialog = false }
        )
    }

    // Edit dialog
    editingItem?.let { item ->
        newsEditDialog(
            item = item,
            onSave = { markdown ->
                scope.launch {
                    news.updateItem(item.id, markdown)
                    editingItem = null
                }
            },
            onClose = { editingItem = null }
        )
    }

    // Delete confirmation
    deletingItem?.let { item ->
        deleteConfirmation(
            title = "Delete News Item",
            message = "Are you sure you want to delete this news item? This action cannot be undone.",
            onConfirm = {
                scope.launch {
                    news.deleteItem(item.id)
                    deletingItem = null
                }
            },
            onCancel = { deletingItem = null }
        )
    }
}

/**
 * A single news item card in the feed.
 *
 * Displays content, metadata, and admin action buttons.
 *
 * @param item The news item to display
 * @param isAdmin Whether the current user is an admin
 * @param onEdit Callback when edit button is clicked
 * @param onDelete Callback when delete button is clicked
 */
@Composable
private fun newsItemCard(
    item: NewsItem,
    isAdmin: Boolean,
    onEdit: () -> Unit,
    onDelete: () -> Unit
) {
    Div({ classes(NewsPageStyles.newsItem) }) {
        box({
            paddedContent = true
        }) {
            // Admin actions
            if (isAdmin) {
                Div({ classes(NewsPageStyles.newsItemHeader) }) {
                    Div({ classes(NewsPageStyles.newsItemActions) }) {
                        // Edit button
                        Div({
                            classes(NewsPageStyles.actionButton)
                            title("Edit")
                            onClick { onEdit() }
                        }) {
                            svgIcon(SVGIcons.pencil) {
                                size = 18.px
                            }
                        }

                        // Delete button
                        Div({
                            classes(NewsPageStyles.actionButton, NewsPageStyles.deleteButton)
                            title("Delete")
                            onClick { onDelete() }
                        }) {
                            svgIcon(SVGIcons.trash) {
                                size = 18.px
                            }
                        }
                    }
                }
            }

            // Content
            Div({ classes(NewsPageStyles.newsItemContent) }) {
                MarkdownText(item.markdown)
            }

            // Metadata
            Div({ classes(NewsPageStyles.newsItemMetadata) }) {
                val createdDate = formatNewsDate(item.createdAt)
                val updatedBy = item.updatedBy ?: ""

                if (item.updatedAt != null && item.updatedAt != item.createdAt) {
                    val updatedDate = formatNewsDate(item.updatedAt!!)
                    Text("Created $createdDate | Updated $updatedDate")
                    if (updatedBy.isNotBlank()) {
                        Text(" by $updatedBy")
                    }
                } else {
                    Text("Posted $createdDate")
                    if (updatedBy.isNotBlank()) {
                        Text(" by $updatedBy")
                    }
                }
            }
        }
    }
}

/**
 * Dialog for creating or editing a news item.
 *
 * @param item The item to edit, or null for creating a new item
 * @param onSave Callback with markdown content when saved
 * @param onClose Callback when dialog is closed
 */
@Composable
private fun newsEditDialog(
    item: NewsItem?,
    onSave: (markdown: String) -> Unit,
    onClose: () -> Unit
) {
    var markdown by remember { mutableStateOf(item?.markdown ?: "") }

    dialogOverlay {
        this.title = if (item == null) "Create News Item" else "Edit News Item"
        closeActionTitle = "Cancel"

        content {
            Div({
                style {
                    padding(1.cssRem)
                }
            }) {
                dialogField("Content", "Use markdown for formatting (headers, lists, links, etc.)") {
                    TextArea(markdown) {
                        classes(NewsPageStyles.editorTextarea)
                        rows(12)
                        attr("placeholder", "Enter news content...")
                        onInput { event ->
                            markdown = event.value
                        }
                    }
                }
            }
        }

        save {
            if (markdown.isNotBlank()) {
                onSave(markdown)
            }
        }

        onClose {
            onClose()
        }
    }
}

/**
 * Simple delete confirmation dialog.
 *
 * @param title Dialog title
 * @param message Confirmation message
 * @param onConfirm Callback when confirmed
 * @param onCancel Callback when cancelled
 */
@Composable
private fun deleteConfirmation(
    title: String,
    message: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    dialogOverlay {
        this.title = title
        closeActionTitle = "Cancel"

        content {
            Div({
                style {
                    padding(1.cssRem)
                }
            }) {
                Text(message)
            }
        }

        action {
            this.title = "Delete"
            this.primary = true
            this.action {
                onConfirm()
            }
        }

        onClose {
            onCancel()
        }
    }
}
